import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { MenuItem, TextField, Checkbox, FormControlLabel, ToggleButtonGroup, ToggleButton } from '@mui/material';
import EventoService from '../../services/eventoService';
import NotificacionService from '../../services/notificacionService';
import CrearEventoModal from '../../components/CrearEventoModal';
import ChecklistTramite from '../../components/Tramite/ChecklistTramite';
import BotonCancelarReactivarTramite from '../../components/Tramite/BotonCancelarReactivarTramite';
import TarjetaCobroTramite from '../../components/Cobranzas/TarjetaCobroTramite';
import SeccionCobranzaTramite from '../../components/Cobranzas/SeccionCobranzaTramite';
import HistorialTramite from '../../components/HistorialTramite';
import PanelAccionesAvanzadasTramite from '../../components/Administracion/PanelAccionesAvanzadasTramite';
import BannerEstadoFinal from '../../components/BannerEstadoFinal';
import WhatsAppHeaderButton from '../../components/WhatsAppHeaderButton';
import PanelUbicacionTramite from '../../components/PanelUbicacionTramite';
import FeedbackButton from '../../components/FeedbackButton';
import NotaComentario from '../../components/NotaComentario';
import TarjetaTurnoAnses from '../../components/TarjetaTurnoAnses';
import ExpedientesAnsesTramite, { enSeguimientoDeExpediente } from '../../components/ExpedientesAnsesTramite';
import useCustomModal from '../../hooks/useCustomModal';
import useVolverAtras from '../../hooks/useVolverAtras';
import BotonGenerarCaratula from '../../components/Caratula/BotonGenerarCaratula';
import CustomModal from '../../components/CustomModal';
import { useAuth } from '../../auth/AuthContext';
import { ModalCrearTurno } from '../Recepcion/TurneroPage';
import turneroStyles from '../../style/pages/Turnero.module.css';
import { getTramitePensionById, actualizarChecklistItem, actualizarTramitePension, actualizarComentariosPension, actualizarMoratoriaPension, iniciarNulidadPension } from '../../services/pensionService';
import { subirArchivo, descargarArchivo, obtenerArchivosPorTramite, eliminarArchivo } from '../../services/archivoService';
import { getDomicilioDniCliente } from '../../services/clienteService';
import { apellidoNombre } from '../../utils/nombrePersona';
import { tipoEstado } from '../../utils/flujoEstados';
import EtiquetaTramite from '../../components/EtiquetaTramite';
import ChipAmparoIniciado from '../../components/Amparo/ChipAmparoIniciado';

const formatLocalFecha = (fechaInput) => {
    if (!fechaInput) return '';
    if (typeof fechaInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaInput)) return fechaInput;
    const d = new Date(fechaInput);
    if (Number.isNaN(d.getTime())) return '';
    return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
};

// Clase del badge contador (X/Y): verde cuando está completo, gris en caso contrario.
const claseBadgeContador = (completados, total) => (total > 0 && completados === total ? 'bg-success' : 'bg-secondary');

const ETIQUETA_MODALIDAD = {
    DIRECTA: 'Pensión Directa',
    DERIVADA: 'Pensión Derivada',
    HIJO_CON_DISCAPACIDAD: 'Pensión por Hijo con Discapacidad',
};

const ETIQUETA_VINCULO = {
    CONYUGE: 'Cónyuge',
    CONVIVIENTE: 'Conviviente',
    HIJO_MENOR: 'Hijo menor',
    HIJO_CON_DISCAPACIDAD: 'Hijo con discapacidad',
};

const ETIQUETA_MORATORIA = {
    NINGUNA: 'Ninguna',
    LEY_24476: 'Ley 24.476',
    LEY_27705: 'Ley 27.705',
};

const ETIQUETA_AUTONOMO_MONOTRIBUTO = {
    AUTONOMO: 'Autónomo',
    MONOTRIBUTO: 'Monotributo',
};

function VistaDetalladaTramitePension() {
    const { idTramite } = useParams();
    const navigate = useNavigate();
    const volverAtras = useVolverAtras('/tramites');
    const { user } = useAuth();
    const { modalState, hideModal, showModal } = useCustomModal();

    const [tramite, setTramite] = useState(null);
    const [loading, setLoading] = useState(true);

    // Edición de datos del trámite (no el cliente)
    const [editMode, setEditMode] = useState(false);
    const [tramiteEdit, setTramiteEdit] = useState(null);
    const [guardandoDatos, setGuardandoDatos] = useState(false);

    // Domicilio DNI del beneficiario, de referencia para comparar con el del causante
    // (CONYUGE/CONVIVIENTE). Se carga al entrar en modo edición y si cambia el vínculo elegido.
    const [domicilioDniCliente, setDomicilioDniCliente] = useState(null);
    const [cargandoDomicilioDni, setCargandoDomicilioDni] = useState(false);

    // Moratoria del causante: acción puntual colgada de la alerta de regularidad fallida,
    // separada de "Editar datos" (ver TramitePensionService.aplicarDatosPropios).
    const [mostrarSelectorMoratoria, setMostrarSelectorMoratoria] = useState(false);
    const [moratoriaSeleccionada, setMoratoriaSeleccionada] = useState('');
    const [guardandoMoratoria, setGuardandoMoratoria] = useState(false);

    // Nulidad (V232): reclamo administrativo sobre la denegatoria, acción puntual colgada del
    // estado "Cerrado desfavorable" (mismo patrón que la moratoria de arriba).
    const [mostrarFormNulidad, setMostrarFormNulidad] = useState(false);
    const [fechaDenegatoriaForm, setFechaDenegatoriaForm] = useState('');
    const [motivoDenegatoriaForm, setMotivoDenegatoriaForm] = useState('');
    const [guardandoNulidad, setGuardandoNulidad] = useState(false);

    // Herramienta: crear turno prellenado con los datos del cliente del trámite
    const [mostrarModalTurno, setMostrarModalTurno] = useState(false);

    const [eventos, setEventos] = useState([]);
    const [cargandoEventos, setCargandoEventos] = useState(true);
    const [mostrarModalEvento, setMostrarModalEvento] = useState(false);
    const [fechaSeleccionadaEvento, setFechaSeleccionadaEvento] = useState(new Date());

    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
    const [subiendoItem, setSubiendoItem] = useState(null);

    // Archivos del trámite (fuente única: el checklist enlaza por idArchivo; "otros" son los no vinculados)
    const [archivos, setArchivos] = useState([]);
    const [subiendoGeneral, setSubiendoGeneral] = useState(false);

    const recargarTramite = useCallback(async () => {
        try {
            const data = await getTramitePensionById(idTramite);
            setTramite(data);
        } catch (error) {
            console.error('Error al cargar trámite de Pensión:', error);
        }
    }, [idTramite]);

    const recargarArchivos = useCallback(async () => {
        try {
            const data = await obtenerArchivosPorTramite(idTramite);
            setArchivos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar archivos:', error);
        }
    }, [idTramite]);

    const recargarEventos = useCallback(async () => {
        try {
            const response = await EventoService.getEventosPorTramite(idTramite);
            setEventos(response.data || []);
        } catch (error) {
            console.error('Error recargando eventos:', error);
        }
    }, [idTramite]);

    // Cargar o cancelar un turno ANSES mueve el estado del trámite del lado del backend
    // (TurnoAnsesService.moverEstadoTramite), así que no alcanza con recargar los eventos:
    // sin recargar la carpeta, el badge de estado y el banner de cierre quedan viejos hasta un F5.
    const refrescarPorTurnoAnses = useCallback(async () => {
        await Promise.all([recargarTramite(), recargarEventos()]);
    }, [recargarTramite, recargarEventos]);

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            setCargandoEventos(true);
            try {
                const [tramiteResult, eventosResult, archivosResult] = await Promise.allSettled([
                    getTramitePensionById(idTramite),
                    EventoService.getEventosPorTramite(idTramite),
                    obtenerArchivosPorTramite(idTramite),
                ]);
                setTramite(tramiteResult.status === 'fulfilled' ? tramiteResult.value : null);
                setEventos(eventosResult.status === 'fulfilled' ? (eventosResult.value.data || []) : []);
                setArchivos(archivosResult.status === 'fulfilled' && Array.isArray(archivosResult.value) ? archivosResult.value : []);
            } finally {
                setLoading(false);
                setCargandoEventos(false);
            }
        };
        cargar();
    }, [idTramite]);

    // Auto-refresco de eventos (paridad con ART/RTI/Jubilación/PUAM/PNC)
    useEffect(() => {
        const handleEventoCreado = () => recargarEventos();
        window.addEventListener('eventoCreadoDesdeChat', handleEventoCreado);
        const interval = setInterval(() => recargarEventos(), 30000);
        return () => {
            window.removeEventListener('eventoCreadoDesdeChat', handleEventoCreado);
            clearInterval(interval);
        };
    }, [recargarEventos]);

    // Domicilio DNI del beneficiario: solo se necesita en modo edición y cuando el vínculo elegido
    // es CONYUGE/CONVIVIENTE (comparación con el domicilio del causante).
    useEffect(() => {
        const vinculoEdit = tramiteEdit?.vinculoBeneficiario;
        const cuilCliente = tramite?.cliente?.cuil;
        const requiereComparacion = editMode && cuilCliente && (vinculoEdit === 'CONYUGE' || vinculoEdit === 'CONVIVIENTE');
        if (!requiereComparacion) {
            setDomicilioDniCliente(null);
            return;
        }
        let activo = true;
        const cargar = async () => {
            setCargandoDomicilioDni(true);
            try {
                const data = await getDomicilioDniCliente(cuilCliente);
                if (activo) setDomicilioDniCliente(data?.domicilioFormateado || null);
            } catch (err) {
                console.error('Error al obtener el domicilio DNI del cliente:', err);
                if (activo) setDomicilioDniCliente(null);
            } finally {
                if (activo) setCargandoDomicilioDni(false);
            }
        };
        cargar();
        return () => { activo = false; };
    }, [editMode, tramiteEdit?.vinculoBeneficiario, tramite?.cliente?.cuil]);

    const handleGuardarEvento = async (evento) => {
        try {
            const responseEvento = await EventoService.crearEvento({
                ...evento,
                fecha: formatLocalFecha(evento.fecha),
                idTramite: Number(idTramite),
            });
            const idEventoCreado = responseEvento.data.idEvento || responseEvento.data;
            if (evento.tiemposRecordatorio && evento.tiemposRecordatorio.length > 0) {
                await NotificacionService.programarNotificacionesAutomaticas(idEventoCreado, evento.tiemposRecordatorio);
            }
            window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', { detail: { timestamp: Date.now(), source: 'tramite-pension' } }));
            await recargarEventos();
            setMostrarModalEvento(false);
            toast.success('Evento creado correctamente');
        } catch (error) {
            console.error('Error al crear evento:', error);
            toast.error('Error al crear el evento');
        }
    };

    const formatearFechaEvento = (fechaString) => {
        if (!fechaString) return '';
        const [y, m, d] = fechaString.split('T')[0].split('-');
        return `${d}/${m}/${y}`;
    };

    // ---- Descarga con nombre real (extensión + tipo correctos) ----
    const descargarPorId = async (idArchivo) => {
        try {
            const meta = archivos.find(a => a.idArchivo === idArchivo);
            const nombre = meta?.nombre || `archivo_${idArchivo}`;
            const resp = await descargarArchivo(idArchivo);
            const blob = meta?.tipo ? new Blob([resp.data], { type: meta.tipo }) : new Blob([resp.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error al descargar archivo:', err);
            toast.error('No se pudo descargar el archivo');
        }
    };

    // ---- Checklist ----
    const togglePresentado = async (item) => {
        if (item.idArchivo) return; // con archivo adjunto, el ítem queda marcado y bloqueado
        try {
            await actualizarChecklistItem(item.id, { presentado: !item.presentado, idArchivo: null });
            recargarTramite();
        } catch (err) {
            console.error('Error al actualizar checklist:', err);
        }
    };
    const adjuntarArchivo = async (item, file) => {
        if (!file) return;
        setSubiendoItem(item.id);
        try {
            const resp = await subirArchivo(idTramite, file);
            const idArchivo = resp?.idArchivo ?? null;
            await actualizarChecklistItem(item.id, { presentado: true, idArchivo });
            await Promise.all([recargarTramite(), recargarArchivos()]);
        } catch (err) {
            console.error('Error al adjuntar archivo:', err);
            toast.error('No se pudo adjuntar el archivo');
        } finally {
            setSubiendoItem(null);
        }
    };
    const quitarAdjunto = (item) => {
        showModal({
            title: 'Eliminar documento',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: `¿Seguro que querés eliminar el archivo adjunto de "${item.nombre}"?`,
            onConfirm: async () => {
                try {
                    await eliminarArchivo(item.idArchivo);
                    await actualizarChecklistItem(item.id, { presentado: false, idArchivo: null });
                    await Promise.all([recargarTramite(), recargarArchivos()]);
                    toast.info('Documento eliminado');
                } catch (err) {
                    console.error('Error al eliminar documento:', err);
                    toast.error('No se pudo eliminar el documento');
                }
            },
        });
    };

    // ---- Otros documentos (no vinculados al checklist) ----
    const subirGeneral = async (file) => {
        if (!file) return;
        setSubiendoGeneral(true);
        try {
            await subirArchivo(idTramite, file);
            await recargarArchivos();
            toast.success('Archivo subido');
        } catch (err) {
            console.error('Error al subir archivo:', err);
            toast.error('No se pudo subir el archivo');
        } finally {
            setSubiendoGeneral(false);
        }
    };
    const eliminarGeneral = (idArchivo) => {
        showModal({
            title: 'Eliminar archivo',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: '¿Seguro que querés eliminar este archivo? Esta acción no se puede deshacer.',
            onConfirm: async () => {
                try {
                    await eliminarArchivo(idArchivo);
                    await recargarArchivos();
                    toast.info('Archivo eliminado');
                } catch (err) {
                    console.error('Error al eliminar archivo:', err);
                    toast.error('No se pudo eliminar el archivo');
                }
            },
        });
    };

    // ---- Comentarios (nota editable con firma, igual que ART/RTI/Jubilación/PUAM/PNC) ----
    const handleGuardarComentario = async (nuevoComentario) => {
        try {
            await actualizarComentariosPension(idTramite, nuevoComentario);
            await recargarTramite();
            toast.success('Comentario actualizado correctamente');
        } catch (error) {
            console.error('Error al guardar comentario:', error);
            toast.error('No se pudo guardar el comentario');
            throw error;
        }
    };

    // ---- Editar datos del trámite ----
    // A diferencia de PUAM/PNC (planos), acá cambiar tipoPension/vinculoBeneficiario puede
    // cambiar el checklist requerido: el backend regenera solo lo que falte, sin tocar lo ya
    // presentado (TramitePensionService.actualizarTramite).
    const handleModificar = () => {
        setTramiteEdit({
            organismo: tramite.organismo || '',
            etiqueta: tramite.etiqueta || '',
            tipoPension: tramite.tipoPension || 'DIRECTA',
            vinculoBeneficiario: tramite.vinculoBeneficiario || 'CONYUGE',
            numeroBeneficio: tramite.numeroBeneficio || '',
            causanteApellidoNombre: tramite.causanteApellidoNombre || '',
            causanteDniCuil: tramite.causanteDniCuil || '',
            causanteFechaNacimiento: formatLocalFecha(tramite.causanteFechaNacimiento),
            causanteFechaDefuncion: formatLocalFecha(tramite.causanteFechaDefuncion),
            causanteDomicilio: tramite.causanteDomicilio || '',
            domicilioCausanteCoincide: tramite.domicilioCausanteCoincide ?? null,
            causanteRelacionDependencia: tramite.causanteRelacionDependencia || '',
            causanteAutonomoMonotributo: tramite.causanteAutonomoMonotributo || '',
            causanteCuentaAfip: tramite.causanteCuentaAfip || '',
            aniosConvivencia: tramite.aniosConvivencia ?? '',
            hijosReconocidosAmbos: !!tramite.hijosReconocidosAmbos,
            cudVigente: !!tramite.cudVigente,
            fechaVencimientoCud: formatLocalFecha(tramite.fechaVencimientoCud),
            fechaCertificacionCud: formatLocalFecha(tramite.fechaCertificacionCud),
            acreditaDependenciaEconomica: !!tramite.acreditaDependenciaEconomica,
            fallecimientoAcreditadoAnses: !!tramite.fallecimientoAcreditadoAnses,
            observacionesCaratula: tramite.observacionesCaratula || '',
            causanteRegularidad: tramite.causanteRegularidad || '',
            causanteCumpleRegularidad: tramite.causanteCumpleRegularidad,
        });
        setEditMode(true);
    };
    const handleGuardarDatos = async () => {
        setGuardandoDatos(true);
        try {
            await actualizarTramitePension(idTramite, {
                ...tramiteEdit,
                organismo: tramiteEdit.organismo || null,
                numeroBeneficio: tramiteEdit.numeroBeneficio || null,
                causanteFechaNacimiento: tramiteEdit.causanteFechaNacimiento || null,
                causanteFechaDefuncion: tramiteEdit.causanteFechaDefuncion || null,
                fechaVencimientoCud: tramiteEdit.fechaVencimientoCud || null,
                fechaCertificacionCud: tramiteEdit.fechaCertificacionCud || null,
                aniosConvivencia: tramiteEdit.aniosConvivencia === '' ? null : Number(tramiteEdit.aniosConvivencia),
                causanteRegularidad: tramiteEdit.causanteRegularidad || null,
            });
            await recargarTramite();
            setEditMode(false);
            toast.success('Datos del trámite actualizados');
        } catch (error) {
            console.error('Error al guardar datos:', error);
            toast.error(error.response?.data || 'No se pudieron guardar los datos');
        } finally {
            setGuardandoDatos(false);
        }
    };

    // ---- Moratoria del causante (acción puntual desde la alerta de regularidad fallida) ----
    const handleGuardarMoratoria = async () => {
        setGuardandoMoratoria(true);
        try {
            await actualizarMoratoriaPension(idTramite, moratoriaSeleccionada || null);
            await recargarTramite();
            setMostrarSelectorMoratoria(false);
            toast.success('Moratoria actualizada');
        } catch (error) {
            console.error('Error al guardar la moratoria:', error);
            toast.error(error.response?.data || 'No se pudo guardar la moratoria');
        } finally {
            setGuardandoMoratoria(false);
        }
    };

    // ---- Nulidad (acción puntual desde el estado "Cerrado desfavorable") ----
    const handleIniciarNulidad = () => {
        showModal({
            title: 'Iniciar Nulidad',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, iniciar nulidad',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: 'El trámite va a pasar a "Nulidad Iniciada" y el checklist de formularios se reinicia sin presentar (los archivos ya subidos no se pierden: van a quedar sueltos en "Otros documentos"). ¿Continuar?',
            onConfirm: async () => {
                setGuardandoNulidad(true);
                try {
                    await iniciarNulidadPension(idTramite, {
                        fechaDenegatoria: fechaDenegatoriaForm || null,
                        motivoDenegatoria: motivoDenegatoriaForm || null,
                    });
                    await recargarTramite();
                    setMostrarFormNulidad(false);
                    toast.success('Nulidad iniciada correctamente');
                } catch (error) {
                    console.error('Error al iniciar la nulidad:', error);
                    toast.error(error.response?.data || 'No se pudo iniciar la nulidad');
                } finally {
                    setGuardandoNulidad(false);
                }
            },
        });
    };

    const verDetalleCliente = (cuil) => navigate(`/clientes/vista-detallada/${cuil.toString()}`);

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
                <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámite...</p>
            </div>
        );
    }
    if (!tramite) {
        return <div className="alert alert-danger mt-4">No se encontró el trámite.</div>;
    }

    const cliente = tramite.cliente || {};
    const formularios = tramite.formularios || [];
    const idsEnChecklist = new Set(formularios.map(f => f.idArchivo).filter(Boolean));
    const archivosOtros = archivos.filter(a => !idsEnChecklist.has(a.idArchivo));
    const nombrePorId = archivos.reduce((acc, a) => { acc[a.idArchivo] = a.nombre; return acc; }, {});
    const formatTamano = (bytes) => (bytes == null ? '' : (bytes / (1024 * 1024)).toFixed(2) + ' MB');

    // Importa si el CUD estaba vigente EL DÍA del fallecimiento del causante, no hoy: certificado
    // antes (o el mismo día) y, si tiene vencimiento, no vencido antes de esa fecha. Que haya
    // vencido DESPUÉS del fallecimiento no importa (sin vencimiento = CUD permanente).
    const cudNoVigenteAlFallecimiento = tramite.vinculoBeneficiario === 'HIJO_CON_DISCAPACIDAD'
        && !!tramite.fechaCertificacionCud && !!tramite.causanteFechaDefuncion
        && (tramite.fechaCertificacionCud > tramite.causanteFechaDefuncion
            || (!!tramite.fechaVencimientoCud && tramite.fechaVencimientoCud < tramite.causanteFechaDefuncion));
    const domicilioNoCoincide = tramite.vinculoBeneficiario === 'CONYUGE'
        && tramite.domicilioCausanteCoincide === false;
    const requiereAcreditarConvivencia = tramite.vinculoBeneficiario === 'CONVIVIENTE' || domicilioNoCoincide;
    const minimoConvivencia = tramite.hijosReconocidosAmbos ? 2 : 5;
    const convivenciaInsuficiente = requiereAcreditarConvivencia
        && tramite.aniosConvivencia != null && tramite.aniosConvivencia < minimoConvivencia;

    return (
        <div className="container mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-1">
                        <h2 className="fw-bold text-primary mb-0">Detalles del Trámite de Pensión</h2>
                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle rounded-pill fs-6 px-3">
                            {ETIQUETA_MODALIDAD[tramite.modalidad] || 'Pensión'}
                        </span>
                        <EtiquetaTramite etiqueta={tramite.etiqueta} />
                        {/* Si sobre este expediente se promovio un amparo por mora ante ANSES, avisa
                            y linkea a su ficha (V239). Es lo que el Excel anota a mano como
                            "AMPARO INICIADO" sobre la fila del tramite. */}
                        <ChipAmparoIniciado idTramite={tramite.idTramite} />
                        {tramite.esNulidad && (
                            <span className="badge rounded-pill fs-6 px-3 fw-bold" style={{ background: '#d97706', color: '#fff' }}>
                                <i className="bi bi-arrow-repeat me-1"></i>Nulidad
                            </span>
                        )}
                    </div>
                    {cliente?.apellido && cliente?.nombre && (
                        <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                            <h5 className="text-secondary fw-semibold mb-0">
                                <PersonSearchIcon fontSize="small" className="me-1 mb-1" />
                                {apellidoNombre(cliente.apellido, cliente.nombre)}
                            </h5>
                            {cliente.telefonoPropio && (
                                <WhatsAppHeaderButton
                                    phone={cliente.telefonoPropio}
                                    clientName={`${cliente.nombre} ${cliente.apellido}`}
                                    onClick={() => window.dispatchEvent(
                                        new CustomEvent('openAdminSidebar', { detail: { tab: 'whatsapp' } })
                                    )}
                                />
                            )}
                        </div>
                    )}
                    <PanelUbicacionTramite tramite={tramite} onUpdated={setTramite} />
                </div>
                <div className="d-flex gap-2 mt-1 align-items-center flex-wrap">
                    <BotonGenerarCaratula
                        tipo="PENSION"
                        idTramite={idTramite}
                        onGenerado={recargarArchivos}
                    />
                    <button
                        className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill"
                        onClick={() => setMostrarModalTurno(true)}
                    >
                        <i className="bi bi-calendar-plus"></i> Crear turno
                    </button>
                    <button
                        className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill"
                        onClick={volverAtras}
                    >
                        <ArrowBackIcon fontSize="small" /> Volver
                    </button>
                </div>
            </div>

            {/* Banner de Nulidad: bien visible (a diferencia del badge, que es un recordatorio
                chico), se muestra siempre que el trámite sea un reclamo de nulidad, en cualquier
                estado en el que esté. */}
            {tramite.esNulidad && (
                <div className="rounded-4 shadow-lg p-3 mb-3 d-flex align-items-center"
                    style={{ background: 'var(--gradient-warning)', color: 'white' }}>
                    <i className="bi bi-arrow-repeat me-3" style={{ fontSize: '2.2rem' }}></i>
                    <div>
                        <h5 className="fw-bold mb-0">Reclamo de Nulidad</h5>
                        <p className="mb-0" style={{ fontSize: '0.9rem', opacity: 0.95 }}>
                            Este trámite es un reclamo administrativo de nulidad sobre una denegatoria anterior.
                        </p>
                    </div>
                </div>
            )}

            {/* Alertas propias del módulo — no bloqueantes */}
            {!tramite.fallecimientoAcreditadoAnses && (
                <div className="alert alert-warning border-0 rounded-3 mb-3">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    El fallecimiento del causante todavía no está acreditado en Mi ANSES.
                </div>
            )}
            {cudNoVigenteAlFallecimiento && (
                <div className="alert alert-danger border-0 rounded-3 mb-3">
                    <i className="bi bi-exclamation-octagon-fill me-2"></i>
                    El CUD no estaba vigente el día del fallecimiento del causante (se certificó después,
                    o ya había vencido antes): no acredita la discapacidad al momento del deceso. Hace
                    falta acreditarla con estudios y certificados médicos anteriores a esa fecha.
                </div>
            )}
            {domicilioNoCoincide && (
                <div className="alert alert-warning border-0 rounded-3 mb-3">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    El domicilio del causante no coincide con el del beneficiario: ANSES no reconoce la
                    convivencia por presunción. Además de la partida de matrimonio, el trámite requiere
                    acreditar convivencia (Prueba de convivencia).
                </div>
            )}
            {convivenciaInsuficiente && (
                <div className="alert alert-warning border-0 rounded-3 mb-3">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    La convivencia declarada ({tramite.aniosConvivencia} año(s)) está por debajo del mínimo exigido
                    ({minimoConvivencia} años{tramite.hijosReconocidosAmbos ? ', por hijos reconocidos por ambos' : ''}).
                </div>
            )}
            {tramite.tipoPension === 'DIRECTA' && tramite.causanteCumpleRegularidad === false && (
                <div className="alert alert-warning border-0 rounded-3 mb-3">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                        <i className="bi bi-exclamation-triangle-fill"></i>
                        <span>El causante no cumple regularidad de aportes. Evaluar completar los años con moratoria previsional.</span>
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                        <span className="fw-semibold">Moratoria:</span>
                        <span>{ETIQUETA_MORATORIA[tramite.moratoriaCausante] || 'Sin registrar'}</span>
                        {!mostrarSelectorMoratoria ? (
                            <button type="button" className="btn btn-sm btn-warning rounded-pill fw-semibold"
                                onClick={() => { setMoratoriaSeleccionada(tramite.moratoriaCausante || ''); setMostrarSelectorMoratoria(true); }}>
                                <i className="bi bi-pencil-square me-1"></i>Registrar moratoria
                            </button>
                        ) : (
                            <>
                                <select className="form-select form-select-sm" style={{ width: 'auto' }}
                                    value={moratoriaSeleccionada}
                                    onChange={(e) => setMoratoriaSeleccionada(e.target.value)}>
                                    <option value="">Sin especificar</option>
                                    <option value="NINGUNA">Ninguna</option>
                                    <option value="LEY_24476">Ley 24.476</option>
                                    <option value="LEY_27705">Ley 27.705</option>
                                </select>
                                <button type="button" className="btn btn-sm btn-success rounded-pill fw-semibold"
                                    onClick={handleGuardarMoratoria} disabled={guardandoMoratoria}>
                                    {guardandoMoratoria
                                        ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                        : <><i className="bi bi-check-lg me-1"></i>Guardar</>}
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill"
                                    disabled={guardandoMoratoria}
                                    onClick={() => setMostrarSelectorMoratoria(false)}>
                                    Cancelar
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {tipoEstado(tramite.nombreEstadoActual) === 'desfavorable' && (
                <div className="alert alert-warning border-0 rounded-3 mb-3">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                        <i className="bi bi-arrow-repeat"></i>
                        <span>
                            Este trámite fue denegado. Si corresponde, se puede iniciar un reclamo
                            administrativo de nulidad sobre esta misma denegatoria.
                        </span>
                    </div>
                    {!mostrarFormNulidad ? (
                        <div className="mt-2">
                            <button type="button" className="btn btn-sm btn-warning rounded-pill fw-semibold"
                                onClick={() => {
                                    setFechaDenegatoriaForm(formatLocalFecha(tramite.fechaDenegatoria));
                                    setMotivoDenegatoriaForm(tramite.motivoDenegatoria || '');
                                    setMostrarFormNulidad(true);
                                }}>
                                <i className="bi bi-file-earmark-text me-1"></i>Iniciar Nulidad
                            </button>
                        </div>
                    ) : (
                        <div className="mt-2 d-flex flex-column gap-2" style={{ maxWidth: '480px' }}>
                            <div>
                                <label className="form-label small fw-semibold mb-1">Fecha de la denegatoria</label>
                                <input type="date" className="form-control form-control-sm"
                                    value={fechaDenegatoriaForm}
                                    onChange={(e) => setFechaDenegatoriaForm(e.target.value)} />
                            </div>
                            <div>
                                <label className="form-label small fw-semibold mb-1">Motivo de la denegatoria</label>
                                <input type="text" className="form-control form-control-sm"
                                    placeholder='Por ejemplo: "falta de acreditación de convivencia"'
                                    value={motivoDenegatoriaForm}
                                    onChange={(e) => setMotivoDenegatoriaForm(e.target.value)} />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button type="button" className="btn btn-sm btn-success rounded-pill fw-semibold"
                                    onClick={handleIniciarNulidad} disabled={guardandoNulidad}>
                                    {guardandoNulidad
                                        ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                        : <><i className="bi bi-check-lg me-1"></i>Confirmar</>}
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill"
                                    disabled={guardandoNulidad}
                                    onClick={() => setMostrarFormNulidad(false)}>
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <BannerEstadoFinal
                idTramite={tramite.idTramite}
                nombreEstadoActual={tramite.nombreEstadoActual}
            />

            {/* Estado + historial */}
            <div
                className="card shadow-sm border-0 rounded-4 p-4 mb-3"
                style={{ backgroundColor: '#f8f9fa', cursor: 'pointer', transition: 'all 0.2s ease' }}
                onClick={() => setModalHistorialAbierto(true)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e9ecef'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                        <h5 className="fw-bold text-secondary mb-0">Estado</h5>
                        <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle rounded-pill fs-6 px-3">
                            {tramite.nombreEstadoActual || 'Ingreso del Trámite'}
                        </span>
                        {tramite.fechaEstadoActual && (
                            <span className="text-muted small">
                                {new Date(tramite.fechaEstadoActual).toLocaleDateString('es-ES')}
                            </span>
                        )}
                    </div>
                    <span className="text-primary">
                        <i className="bi bi-arrow-right-circle me-1"></i>Ver historial
                    </span>
                </div>
            </div>

            {/* Nota editable de comentarios (con firma, igual que ART/RTI/Jubilación/PUAM/PNC) */}
            <NotaComentario
                valor={tramite.comentarios || ''}
                onGuardar={handleGuardarComentario}
                label="Comentarios del Trámite de Pensión"
                color="blue"
                nombreUsuario={user?.nombre || ''}
            />

            {/* Cobranza del trámite: desplegable, debajo del estado y los comentarios */}
            <SeccionCobranzaTramite tramite={tramite} />

            {/* Turno en ANSES: hito propio del trámite. Al cargarlo se genera la citación al
                estudio en el turnero y el evento de agenda. */}
            <TarjetaTurnoAnses
                idTramite={tramite.idTramite}
                cuilCliente={tramite.idCliente || cliente.cuil}
                nombreArea="este trámite de Pensión"
                nombreEstadoActual={tramite.nombreEstadoActual}
                onCambio={refrescarPorTurnoAnses}
                onEstadoCambiado={refrescarPorTurnoAnses}
            />
            {enSeguimientoDeExpediente(tramite.nombreEstadoActual) && <ExpedientesAnsesTramite idTramite={tramite.idTramite} />}

            {/* Eventos del Trámite */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold text-primary mb-0">Eventos del Trámite</h4>
                            <button
                                className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill fw-semibold"
                                onClick={() => {
                                    const hoy = new Date();
                                    setFechaSeleccionadaEvento(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()));
                                    setMostrarModalEvento(true);
                                }}
                            >
                                <span style={{ fontSize: '1.2rem' }}>+</span> Crear Evento
                            </button>
                        </div>
                        {cargandoEventos ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="mt-2 text-muted">Cargando eventos...</p>
                            </div>
                        ) : eventos.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center">
                                <p className="mb-0">No hay eventos registrados para este trámite</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {eventos.map(evento => (
                                    <div key={evento.idEvento} className="col-md-6 col-lg-4">
                                        <div className="card h-100 border rounded-3 p-3 shadow-sm">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="fw-bold text-primary mb-0">{evento.titulo}</h6>
                                                {evento.tipoEvento && <span className="badge bg-primary rounded-pill">{evento.tipoEvento}</span>}
                                            </div>
                                            {evento.descripcion && <p className="text-muted small mb-2">{evento.descripcion}</p>}
                                            <div className="mt-auto">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <small className="text-muted">📅</small>
                                                    <small className="fw-semibold">{formatearFechaEvento(evento.fecha)}</small>
                                                </div>
                                                {evento.todoElDia ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small className="text-success fw-semibold">Todo el día</small>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small>{evento.horaInicio} - {evento.horaFin}</small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Info del Trámite + Info del Cliente */}
            <div className="row g-4">
                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold text-primary mb-3">
                            <i className="bi bi-person-vcard me-2"></i>
                            Información del Trámite
                        </h4>
                        {!editMode ? (
                            <>
                                <p><strong>Modalidad:</strong> {ETIQUETA_MODALIDAD[tramite.modalidad] || 'Sin especificar'}</p>
                                <p><strong>Vínculo del beneficiario:</strong> {ETIQUETA_VINCULO[tramite.vinculoBeneficiario] || 'Sin especificar'}</p>
                                {tramite.tipoPension === 'DERIVADA' && (
                                    <p><strong>N° de Beneficio:</strong> {tramite.numeroBeneficio || 'Sin especificar'}</p>
                                )}
                                <p><strong>Organismo:</strong> {tramite.organismo === 'ANSES' ? 'ANSES' : tramite.organismo === 'PROVINCIA' ? 'Provincia' : 'Sin especificar'}</p>
                                {tramite.etiqueta && <p><strong>Etiqueta:</strong> {tramite.etiqueta}</p>}

                                {tramite.esNulidad && (
                                    <>
                                        <hr />
                                        <h6 className="fw-bold text-secondary">Nulidad</h6>
                                        <p><strong>Fecha de la denegatoria:</strong> {tramite.fechaDenegatoria ? new Date(tramite.fechaDenegatoria + 'T00:00:00').toLocaleDateString('es-ES') : 'Sin especificar'}</p>
                                        <p><strong>Motivo de la denegatoria:</strong> {tramite.motivoDenegatoria || 'Sin especificar'}</p>
                                    </>
                                )}

                                <hr />
                                <h6 className="fw-bold text-secondary">Datos del Causante</h6>
                                <p><strong>Apellido y Nombre:</strong> {tramite.causanteApellidoNombre || 'Sin especificar'}</p>
                                <p><strong>DNI/CUIL:</strong> {tramite.causanteDniCuil || 'Sin especificar'}</p>
                                <p><strong>Fecha de nacimiento:</strong> {tramite.causanteFechaNacimiento ? new Date(tramite.causanteFechaNacimiento + 'T00:00:00').toLocaleDateString('es-ES') : 'Sin especificar'}</p>
                                <p><strong>Fecha de defunción:</strong> {tramite.causanteFechaDefuncion ? new Date(tramite.causanteFechaDefuncion + 'T00:00:00').toLocaleDateString('es-ES') : 'Sin especificar'}</p>
                                <p><strong>Domicilio:</strong> {tramite.causanteDomicilio || 'Sin especificar'}</p>
                                {(tramite.vinculoBeneficiario === 'CONYUGE' || tramite.vinculoBeneficiario === 'CONVIVIENTE') && (
                                    <p>
                                        <strong>Coincide con domicilio del beneficiario:</strong>{' '}
                                        {tramite.domicilioCausanteCoincide == null ? 'Sin evaluar' : (tramite.domicilioCausanteCoincide ? 'Sí' : 'No')}
                                    </p>
                                )}
                                <p><strong>Relación de dependencia:</strong> {tramite.causanteRelacionDependencia || 'Sin especificar'}</p>
                                <p><strong>Autónomo o monotributo:</strong> {ETIQUETA_AUTONOMO_MONOTRIBUTO[tramite.causanteAutonomoMonotributo] || tramite.causanteAutonomoMonotributo || 'Sin especificar'}</p>
                                <p><strong>Cuenta AFIP:</strong> {tramite.causanteCuentaAfip || 'Sin especificar'}</p>
                                <p><strong>Fallecimiento acreditado en Mi ANSES:</strong> {tramite.fallecimientoAcreditadoAnses ? 'Sí' : 'No'}</p>

                                {tramite.tipoPension === 'DIRECTA' && (
                                    <>
                                        <hr />
                                        <h6 className="fw-bold text-secondary">Regularidad de Aportes del Causante</h6>
                                        <p><strong>Regularidad:</strong> {tramite.causanteRegularidad || 'Sin evaluar'}</p>
                                        <p>
                                            <strong>Cumple regularidad:</strong>{' '}
                                            {tramite.causanteCumpleRegularidad == null ? 'Sin evaluar' : (tramite.causanteCumpleRegularidad ? 'Sí' : 'No')}
                                        </p>
                                    </>
                                )}

                                {requiereAcreditarConvivencia && (
                                    <>
                                        <hr />
                                        <h6 className="fw-bold text-secondary">Acreditación de la Convivencia</h6>
                                        <p><strong>Años de convivencia:</strong> {tramite.aniosConvivencia ?? 'Sin especificar'}</p>
                                        <p><strong>Hijos reconocidos por ambos:</strong> {tramite.hijosReconocidosAmbos ? 'Sí' : 'No'}</p>
                                    </>
                                )}
                                {tramite.vinculoBeneficiario === 'HIJO_CON_DISCAPACIDAD' && (
                                    <>
                                        <hr />
                                        <h6 className="fw-bold text-secondary">Acreditación de la Discapacidad</h6>
                                        <p><strong>Certificación del CUD:</strong> {tramite.fechaCertificacionCud ? new Date(tramite.fechaCertificacionCud + 'T00:00:00').toLocaleDateString('es-ES') : 'Sin especificar'}</p>
                                        <p><strong>Vencimiento del CUD:</strong> {tramite.fechaVencimientoCud ? new Date(tramite.fechaVencimientoCud + 'T00:00:00').toLocaleDateString('es-ES') : 'Sin vencimiento (permanente)'}</p>
                                        <p><strong>CUD vigente en la actualidad:</strong> {tramite.cudVigente ? 'Sí' : 'No'}</p>
                                        <p><strong>Acredita dependencia económica:</strong> {tramite.acreditaDependenciaEconomica ? 'Sí' : 'No'}</p>
                                    </>
                                )}

                                <ChecklistTramite tramite={tramite} onActualizado={recargarTramite} />
                                <TarjetaCobroTramite
                                    tramite={tramite}
                                    onPactoGenerado={() => Promise.all([recargarTramite(), recargarArchivos()])}
                                />
                                <div className="mt-3 d-flex gap-2 flex-wrap">
                                    <button className="btn btn-outline-primary rounded-pill px-4 fw-semibold" onClick={handleModificar}>
                                        <i className="bi bi-pencil me-2"></i>Editar datos
                                    </button>
                                    <BotonCancelarReactivarTramite tramite={tramite} onActualizado={recargarTramite} />
                                </div>
                            </>
                        ) : (
                            <>
                                <TextField select label="Tipo de Pensión" fullWidth sx={{ mb: 2 }}
                                    value={tramiteEdit.tipoPension}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, tipoPension: e.target.value }))}>
                                    <MenuItem value="DIRECTA">Directa (causante en actividad)</MenuItem>
                                    <MenuItem value="DERIVADA">Derivada (causante jubilado)</MenuItem>
                                </TextField>
                                <TextField select label="Vínculo del beneficiario" fullWidth sx={{ mb: 2 }}
                                    value={tramiteEdit.vinculoBeneficiario}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, vinculoBeneficiario: e.target.value }))}>
                                    <MenuItem value="CONYUGE">Cónyuge</MenuItem>
                                    <MenuItem value="CONVIVIENTE">Conviviente</MenuItem>
                                    <MenuItem value="HIJO_MENOR">Hijo menor</MenuItem>
                                    <MenuItem value="HIJO_CON_DISCAPACIDAD">Hijo con discapacidad</MenuItem>
                                </TextField>
                                {tramiteEdit.tipoPension === 'DERIVADA' && (
                                    <TextField label="N° de Beneficio" fullWidth sx={{ mb: 2 }}
                                        value={tramiteEdit.numeroBeneficio}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, numeroBeneficio: e.target.value }))} />
                                )}
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Organismo</label>
                                    <select className="form-select" value={tramiteEdit.organismo || ''}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, organismo: e.target.value }))}>
                                        <option value="">Sin especificar</option>
                                        <option value="ANSES">ANSES</option>
                                        <option value="PROVINCIA">Provincia</option>
                                    </select>
                                </div>
                                <TextField label="Etiqueta" fullWidth sx={{ mb: 2 }}
                                    value={tramiteEdit.etiqueta}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, etiqueta: e.target.value }))} />

                                <hr />
                                <h6 className="fw-bold text-secondary mb-2">Datos del Causante</h6>
                                <TextField label="Apellido y Nombre" fullWidth sx={{ mb: 2 }}
                                    value={tramiteEdit.causanteApellidoNombre}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, causanteApellidoNombre: e.target.value }))} />
                                <TextField label="DNI/CUIL" fullWidth sx={{ mb: 2 }}
                                    value={tramiteEdit.causanteDniCuil}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, causanteDniCuil: e.target.value }))} />
                                <TextField label="Fecha de nacimiento" type="date" fullWidth sx={{ mb: 2 }} InputLabelProps={{ shrink: true }}
                                    value={tramiteEdit.causanteFechaNacimiento}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, causanteFechaNacimiento: e.target.value }))} />
                                <TextField label="Fecha de defunción" type="date" fullWidth sx={{ mb: 2 }} InputLabelProps={{ shrink: true }}
                                    value={tramiteEdit.causanteFechaDefuncion}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, causanteFechaDefuncion: e.target.value }))} />
                                <TextField label="Domicilio" fullWidth sx={{ mb: 2 }}
                                    value={tramiteEdit.causanteDomicilio}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, causanteDomicilio: e.target.value }))} />
                                {(tramiteEdit.vinculoBeneficiario === 'CONYUGE' || tramiteEdit.vinculoBeneficiario === 'CONVIVIENTE') && (
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold d-flex align-items-center gap-2 mb-1">
                                            <i className="bi bi-geo-alt text-secondary"></i>Domicilio DNI del beneficiario
                                        </label>
                                        <p className="text-muted small mb-2">
                                            {cargandoDomicilioDni
                                                ? 'Buscando...'
                                                : (domicilioDniCliente || 'El cliente no tiene domicilio DNI cargado.')}
                                        </p>
                                        <label className="form-label fw-semibold d-block mb-1">
                                            ¿Coincide con el domicilio del causante?
                                        </label>
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={tramiteEdit.domicilioCausanteCoincide}
                                            onChange={(_e, valor) => setTramiteEdit(p => ({ ...p, domicilioCausanteCoincide: valor }))}
                                        >
                                            <ToggleButton value={true}>Coincide</ToggleButton>
                                            <ToggleButton value={false}>No coincide</ToggleButton>
                                        </ToggleButtonGroup>
                                    </div>
                                )}
                                <TextField label="Relación de dependencia" fullWidth sx={{ mb: 2 }}
                                    value={tramiteEdit.causanteRelacionDependencia}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, causanteRelacionDependencia: e.target.value }))} />
                                <TextField select label="Autónomo o monotributo" fullWidth sx={{ mb: 2 }}
                                    value={tramiteEdit.causanteAutonomoMonotributo || ''}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, causanteAutonomoMonotributo: e.target.value }))}>
                                    <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                    <MenuItem value="AUTONOMO">Autónomo</MenuItem>
                                    <MenuItem value="MONOTRIBUTO">Monotributo</MenuItem>
                                </TextField>
                                <TextField label="Cuenta AFIP" fullWidth sx={{ mb: 2 }}
                                    value={tramiteEdit.causanteCuentaAfip}
                                    onChange={(e) => setTramiteEdit(p => ({ ...p, causanteCuentaAfip: e.target.value }))} />
                                <FormControlLabel
                                    control={<Checkbox checked={!!tramiteEdit.fallecimientoAcreditadoAnses}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, fallecimientoAcreditadoAnses: e.target.checked }))} />}
                                    label="Fallecimiento acreditado en Mi ANSES"
                                />

                                {tramiteEdit.tipoPension === 'DIRECTA' && (
                                    <>
                                        <hr />
                                        <h6 className="fw-bold text-secondary mb-2">Regularidad de Aportes del Causante</h6>
                                        <TextField label="Regularidad" fullWidth sx={{ mb: 2 }}
                                            value={tramiteEdit.causanteRegularidad}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, causanteRegularidad: e.target.value }))}
                                            helperText='Vía de cumplimiento, ej. "30/36".' />
                                        <TextField select label="Cumple regularidad" fullWidth sx={{ mb: 2 }}
                                            value={tramiteEdit.causanteCumpleRegularidad == null ? '' : String(tramiteEdit.causanteCumpleRegularidad)}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, causanteCumpleRegularidad: e.target.value === '' ? null : e.target.value === 'true' }))}>
                                            <MenuItem value=""><em>Sin evaluar</em></MenuItem>
                                            <MenuItem value="true">Sí</MenuItem>
                                            <MenuItem value="false">No</MenuItem>
                                        </TextField>
                                    </>
                                )}

                                {(tramiteEdit.vinculoBeneficiario === 'CONVIVIENTE'
                                    || (tramiteEdit.vinculoBeneficiario === 'CONYUGE' && tramiteEdit.domicilioCausanteCoincide === false)) && (
                                    <>
                                        <hr />
                                        <h6 className="fw-bold text-secondary mb-2">Acreditación de la Convivencia</h6>
                                        <TextField label="Años de convivencia" type="number" fullWidth sx={{ mb: 2 }}
                                            value={tramiteEdit.aniosConvivencia}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, aniosConvivencia: e.target.value }))} />
                                        <FormControlLabel
                                            control={<Checkbox checked={!!tramiteEdit.hijosReconocidosAmbos}
                                                onChange={(e) => setTramiteEdit(p => ({ ...p, hijosReconocidosAmbos: e.target.checked }))} />}
                                            label="Hijos reconocidos por ambos"
                                        />
                                    </>
                                )}
                                {tramiteEdit.vinculoBeneficiario === 'HIJO_CON_DISCAPACIDAD' && (
                                    <>
                                        <hr />
                                        <h6 className="fw-bold text-secondary mb-2">Acreditación de la Discapacidad</h6>
                                        <TextField label="Fecha de certificación del CUD" type="date" fullWidth sx={{ mb: 2 }} InputLabelProps={{ shrink: true }}
                                            value={tramiteEdit.fechaCertificacionCud}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, fechaCertificacionCud: e.target.value }))}
                                            helperText="Tiene que ser anterior o igual al fallecimiento del causante." />
                                        <TextField label="Fecha de vencimiento del CUD" type="date" fullWidth sx={{ mb: 2 }} InputLabelProps={{ shrink: true }}
                                            value={tramiteEdit.fechaVencimientoCud}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, fechaVencimientoCud: e.target.value }))}
                                            helperText="Dejar vacío si el CUD no vence (permanente)." />
                                        {!!tramiteEdit.fechaCertificacionCud && !!tramiteEdit.causanteFechaDefuncion
                                            && (tramiteEdit.fechaCertificacionCud > tramiteEdit.causanteFechaDefuncion
                                                || (!!tramiteEdit.fechaVencimientoCud && tramiteEdit.fechaVencimientoCud < tramiteEdit.causanteFechaDefuncion)) && (
                                            <div className="alert alert-danger border-0 rounded-3 mb-3">
                                                <i className="bi bi-exclamation-octagon-fill me-2"></i>
                                                El CUD no estaba vigente el día del fallecimiento del causante (se
                                                certificó después, o ya había vencido antes): no acredita la
                                                discapacidad al momento del deceso.
                                            </div>
                                        )}
                                        <FormControlLabel
                                            control={<Checkbox checked={!!tramiteEdit.cudVigente}
                                                onChange={(e) => setTramiteEdit(p => ({ ...p, cudVigente: e.target.checked }))} />}
                                            label="CUD vigente en la actualidad"
                                        />
                                        <FormControlLabel
                                            control={<Checkbox checked={!!tramiteEdit.acreditaDependenciaEconomica}
                                                onChange={(e) => setTramiteEdit(p => ({ ...p, acreditaDependenciaEconomica: e.target.checked }))} />}
                                            label="Acredita dependencia económica"
                                        />
                                    </>
                                )}

                                <div className="d-flex gap-2 mt-3">
                                    <button className="btn btn-primary rounded-pill px-4 fw-semibold" onClick={handleGuardarDatos} disabled={guardandoDatos}>
                                        {guardandoDatos ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle me-2"></i>Guardar</>}
                                    </button>
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setEditMode(false)}>Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold mb-3" style={{ color: '#6c757d' }}>Información del Cliente</h4>
                        <p><strong>CUIL:</strong> {cliente.cuil || 'N/A'}</p>
                        <p><strong>Nombre:</strong> {cliente.nombre || 'N/A'}</p>
                        <p><strong>Apellido:</strong> {cliente.apellido || 'N/A'}</p>
                        <p><strong>Oficio / Actividad:</strong> {cliente.oficio || 'N/A'}</p>
                        <p><strong>Teléfono:</strong> {cliente.telefonoPropio || 'N/A'}</p>
                        {cliente.fechaNacimiento && (
                            <p><strong>Fecha de nacimiento:</strong> {new Date(cliente.fechaNacimiento).toLocaleDateString('es-ES')}</p>
                        )}
                        {cliente.cuil && (
                            <div className="mt-3 d-flex justify-content-center">
                                <button
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                    onClick={() => verDetalleCliente(cliente.cuil)}
                                >
                                    <PersonSearchIcon fontSize="medium" /> Ver Detalle del Cliente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Expedientes de ANSES: acá, debajo de los datos del trámite y del cliente; arriba solo
                mientras el trámite está en "Seguimiento de Expediente". */}
            {!enSeguimientoDeExpediente(tramite.nombreEstadoActual) && <ExpedientesAnsesTramite idTramite={tramite.idTramite} />}

            {/* Documentación del Trámite: checklist de formularios + otros documentos (un solo lugar) */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <h4 className="fw-bold text-primary mb-3"><i className="bi bi-folder2-open me-2"></i>Documentación del Trámite</h4>

                        {/* Formularios requeridos */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold text-secondary mb-0"><i className="bi bi-check2-square me-2"></i>Formularios requeridos</h6>
                            <span className={`badge ${claseBadgeContador(formularios.filter(f => f.presentado).length, formularios.length)}`}>{formularios.filter(f => f.presentado).length}/{formularios.length}</span>
                        </div>
                        {formularios.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center mb-0">
                                <p className="mb-0">No hay formularios en el checklist.</p>
                            </div>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {formularios.map(f => (
                                    <li key={f.id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2 px-0">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id={`chk-${f.id}`}
                                                checked={!!f.presentado} disabled={!!f.idArchivo}
                                                onChange={() => togglePresentado(f)} />
                                            <label className="form-check-label" htmlFor={`chk-${f.id}`}
                                                style={{ textDecoration: f.presentado ? 'line-through' : 'none', color: f.presentado ? '#6c757d' : 'inherit' }}>
                                                {f.nombre}
                                            </label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {f.idArchivo ? (
                                                <>
                                                    <button className="btn btn-sm btn-outline-primary rounded-pill"
                                                        onClick={() => descargarPorId(f.idArchivo)}
                                                        title={nombrePorId[f.idArchivo] || 'Descargar'}>
                                                        <i className="bi bi-download me-1"></i>
                                                        {nombrePorId[f.idArchivo] || 'Descargar'}
                                                    </button>
                                                    <button className="btn btn-sm btn-link text-danger p-0" title="Quitar adjunto"
                                                        onClick={() => quitarAdjunto(f)}>
                                                        <i className="bi bi-x-circle"></i>
                                                    </button>
                                                </>
                                            ) : (
                                                <label className="btn btn-sm btn-outline-secondary rounded-pill mb-0">
                                                    {subiendoItem === f.id
                                                        ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                                        : <><i className="bi bi-paperclip me-1"></i>Adjuntar</>}
                                                    <input type="file" accept="application/pdf" hidden
                                                        onChange={(e) => adjuntarArchivo(f, e.target.files[0])} />
                                                </label>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <hr className="my-4" />

                        {/* Otros documentos (no vinculados a un formulario) */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="fw-bold text-secondary mb-0"><i className="bi bi-paperclip me-2"></i>Otros documentos</h6>
                            <label className="btn btn-sm btn-outline-primary rounded-pill mb-0">
                                {subiendoGeneral
                                    ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                    : <><i className="bi bi-cloud-upload me-1"></i>Subir archivo</>}
                                <input type="file" hidden onChange={(e) => { subirGeneral(e.target.files[0]); e.target.value = ''; }} />
                            </label>
                        </div>
                        {archivosOtros.length === 0 ? (
                            <p className="text-muted fst-italic mb-0">No hay otros documentos cargados.</p>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {archivosOtros.map(a => (
                                    <div key={a.idArchivo} className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <i className="bi bi-file-earmark-text text-primary" style={{ fontSize: '1.25rem' }}></i>
                                            <div style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-truncate" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    onClick={() => descargarPorId(a.idArchivo)} title={a.nombre}>
                                                    {a.nombre}
                                                </div>
                                                <small className="text-muted">{formatTamano(a.tamaño)}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <button className="btn btn-sm" style={{ color: '#0056b3', border: 'none' }} title="Descargar"
                                                onClick={() => descargarPorId(a.idArchivo)}>
                                                <i className="bi bi-download"></i>
                                            </button>
                                            <button className="btn btn-sm" style={{ color: '#dc3545', border: 'none' }} title="Eliminar"
                                                onClick={() => eliminarGeneral(a.idArchivo)}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal crear evento */}
            <CrearEventoModal
                isOpen={mostrarModalEvento}
                onClose={() => setMostrarModalEvento(false)}
                onSave={handleGuardarEvento}
                selectedDate={fechaSeleccionadaEvento}
                idTramite={idTramite}
            />

            {/* Acciones irreversibles (cambiar tipo / eliminar definitivamente).
                El propio panel decide si mostrarse segun el rol. */}
            <PanelAccionesAvanzadasTramite
                idTramite={tramite?.idTramite}
                tipoTramite="PENSION"
            />

            {/* Modal historial de estados (ámbito PENSION) */}
            <HistorialTramite
                isOpen={modalHistorialAbierto}
                onClose={() => setModalHistorialAbierto(false)}
                idTramite={idTramite}
                onEstadoCambiado={recargarTramite}
                ambitoFiltro="PENSION"
            />

            {/* Modal crear turno (reutiliza el turnero de Recepción, prellenado con el cliente) */}
            {mostrarModalTurno && (
                <div className={turneroStyles.overlay} onClick={() => setMostrarModalTurno(false)}>
                    <div className={turneroStyles.contenidoModal} onClick={(e) => e.stopPropagation()}>
                        <ModalCrearTurno
                            celdaSel={null}
                            valoresIniciales={{
                                nombre: cliente.nombre || '',
                                apellido: cliente.apellido || '',
                                cuil: cliente.cuil || '',
                                telefono: cliente.telefonoPropio || '',
                                tipoTramite: 'PENSION',
                            }}
                            onCerrar={() => setMostrarModalTurno(false)}
                            onCreado={() => { setMostrarModalTurno(false); toast.success('Turno creado correctamente'); }}
                        />
                    </div>
                </div>
            )}

            <CustomModal {...modalState} onHide={hideModal} />
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
            <FeedbackButton tramite={tramite} />
        </div>
    );
}

export default VistaDetalladaTramitePension;
